import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { stApi, errorMessage } from './api'
import { ProctorSession, isSupportedBrowser } from './proctoring'
import { runInBrowser, warmUpRunner } from './browserRunner'
import { McqQuestion, CodeQuestion, TestsQuestion, ReviewQuestion, isAnswered } from './questions'
import './stDiagnostic.css'

const LANGUAGE_LABELS = { python: 'Python', javascript: 'JavaScript' }
const AUTOSAVE_MS = 3000

function formatClock(ms) {
  const total = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function VideoPreview({ stream, className }) {
  const ref = useRef(null)
  useEffect(() => {
    if (ref.current) ref.current.srcObject = stream || null
  }, [stream])
  return <video ref={ref} className={className} autoPlay playsInline muted />
}

// Unlisted URL of the candidate page; only people given the link can find it.
export const ST_DIAGNOSTIC_PATH = '/software-testingfsdg5g4sd4fg744g'

export default function StDiagnostic() {
  const [phase, setPhase] = useState('loading')
  const [error, setError] = useState('')
  const [me, setMe] = useState(null)
  const [language, setLanguage] = useState('python')
  const [consent, setConsent] = useState(false)

  const [attempt, setAttempt] = useState(null)
  const [sections, setSections] = useState([])
  const [clockOffset, setClockOffset] = useState(0)
  const [now, setNow] = useState(Date.now())
  const [answers, setAnswers] = useState({})
  const [hypotheses, setHypotheses] = useState({})
  const [activeSection, setActiveSection] = useState('A')
  const [saveState, setSaveState] = useState('saved')
  const [confirmSubmit, setConfirmSubmit] = useState(false)
  const [confirmExit, setConfirmExit] = useState(false)
  const navigate = useNavigate()
  const [result, setResult] = useState(null)

  const [, setTick] = useState(0)
  const sessionRef = useRef(null)
  if (!sessionRef.current) sessionRef.current = new ProctorSession({ onChange: () => setTick(t => t + 1) })
  const session = sessionRef.current
  const status = session.status()

  const answersRef = useRef(answers)
  answersRef.current = answers
  const dirtyRef = useRef(new Set())
  const submittingRef = useRef(false)

  const browserSupported = isSupportedBrowser()

  // ─── Loading ───────────────────────────────────────────────────────────────

  const loadResult = useCallback(async () => {
    const { data } = await stApi.get('/attempt/result')
    setResult(data.result)
    setPhase('result')
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { data } = await stApi.get('/me')
        if (cancelled) return
        setMe(data)
        if (!data.attempt) setPhase('intro')
        else if (data.attempt.status === 'in_progress') { setLanguage(data.attempt.language); setPhase('setup') }
        else await loadResult()
      } catch (err) {
        if (cancelled) return
        if (err?.response?.status === 401) setPhase('login')
        else { setError(errorMessage(err)); setPhase('error') }
      }
    })()
    return () => { cancelled = true }
  }, [loadResult])

  useEffect(() => () => { sessionRef.current?.stop() }, [])

  // ─── Setup ─────────────────────────────────────────────────────────────────

  const enable = async (fn) => {
    setError('')
    try {
      await fn()
    } catch (err) {
      setError(err.name === 'NotAllowedError' ? 'Permission was denied. Allow access in your browser and try again.' : err.message)
    }
  }

  const startExam = async () => {
    setError('')
    // Fullscreen must be requested directly inside the click, before any await.
    const fullscreen = document.documentElement.requestFullscreen?.().catch(() => {})
    try {
      const resuming = !!me?.attempt
      if (!resuming) await stApi.post('/attempt/start', { language })
      await fullscreen
      session.startMonitoring()
      session.connect().catch(() => setTick(t => t + 1))

      const { data } = await stApi.get('/attempt')
      if (data.attempt.status !== 'in_progress') {
        await session.stop()
        return loadResult()
      }
      setAttempt(data.attempt)
      setSections(data.sections)
      setClockOffset(data.serverTime - Date.now())
      setAnswers(data.attempt.answers || {})
      setHypotheses(data.attempt.hypotheses || {})
      setPhase('exam')
    } catch (err) {
      setError(errorMessage(err, 'Could not start the diagnostic'))
    }
  }

  // ─── Exam: saving, timer, submit ───────────────────────────────────────────

  const flushAnswers = useCallback(async () => {
    const ids = [...dirtyRef.current]
    if (!ids.length) return
    dirtyRef.current.clear()
    setSaveState('saving')
    try {
      const payload = Object.fromEntries(ids.map(id => [id, answersRef.current[id]]))
      await stApi.put('/attempt/answers', { answers: payload })
      setSaveState(dirtyRef.current.size ? 'unsaved' : 'saved')
    } catch (err) {
      ids.forEach(id => dirtyRef.current.add(id))
      setSaveState('error')
      if (err?.response?.data?.expired) {
        await session.stop()
        await loadResult()
      }
    }
  }, [session, loadResult])

  const setAnswer = (qid, value) => {
    setAnswers(prev => ({ ...prev, [qid]: value }))
    dirtyRef.current.add(qid)
    setSaveState('unsaved')
  }

  const submit = useCallback(async () => {
    if (submittingRef.current) return
    submittingRef.current = true
    setConfirmSubmit(false)
    setPhase('submitting')
    try {
      await flushAnswers()
      const { data } = await stApi.post('/attempt/submit')
      await session.stop()
      setResult(data.result)
      setPhase('result')
    } catch (err) {
      submittingRef.current = false
      setError(errorMessage(err, 'Submitting failed — please try again'))
      setPhase('exam')
    }
  }, [flushAnswers, session])

  useEffect(() => {
    if (phase !== 'exam') return
    const save = setInterval(flushAnswers, AUTOSAVE_MS)
    const clock = setInterval(() => setNow(Date.now()), 1000)
    const warn = (e) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', warn)
    return () => { clearInterval(save); clearInterval(clock); window.removeEventListener('beforeunload', warn) }
  }, [phase, flushAnswers])

  // The browser Back button would leave the exam (and stop the camera and screen share).
  // Keep an extra history entry so Back asks first instead.
  useEffect(() => {
    if (phase !== 'exam') return
    window.history.pushState({ stExamGuard: true }, '', window.location.href)
    const onBack = () => {
      window.history.pushState({ stExamGuard: true }, '', window.location.href)
      setConfirmExit(true)
    }
    window.addEventListener('popstate', onBack)
    return () => window.removeEventListener('popstate', onBack)
  }, [phase])

  const leaveExam = async () => {
    setConfirmExit(false)
    await flushAnswers()
    await session.stop()
    navigate('/')
  }

  const remaining = attempt ? new Date(attempt.deadline).getTime() - (now + clockOffset) : 0
  useEffect(() => {
    if (phase === 'exam' && attempt && remaining <= 0) submit()
  }, [phase, attempt, remaining, submit])

  const saveHypothesis = async (qid, text) => {
    try {
      const { data } = await stApi.post('/attempt/hypothesis', { questionId: qid, text })
      setHypotheses(prev => ({ ...prev, [qid]: data.hypothesis }))
    } catch (err) {
      throw new Error(errorMessage(err))
    }
  }

  // ▶ Run executes in the candidate's browser; the run is then recorded for the reviewer.
  // Hidden tests are run later, in an admin's browser.
  const runCode = async (q, code, customStdin) => {
    const sample = q.type === 'debug' ? q.sample : q.samples[0]
    const custom = customStdin !== undefined
    const stdin = custom ? customStdin : sample.input
    const { output, error } = await runInBrowser(attempt.language, code, stdin)
    const matches = custom ? null : !error && output.replace(/\r/g, '').trim() === sample.expected
    stApi.post('/attempt/run-log', { questionId: q.id, code, stdin, customInput: custom, output, matches, error }).catch(() => {})
    return { output, error, matches }
  }

  useEffect(() => {
    if (phase === 'exam' && attempt) warmUpRunner(attempt.language)
  }, [phase, attempt])

  const progress = useMemo(() => {
    const bySection = {}
    let answered = 0
    let total = 0
    for (const s of sections) {
      const done = s.questions.filter(q => isAnswered(q, answers[q.id], hypotheses[q.id])).length
      bySection[s.id] = { done, total: s.questions.length }
      answered += done
      total += s.questions.length
    }
    return { bySection, answered, total }
  }, [sections, answers, hypotheses])

  // ─── Render ────────────────────────────────────────────────────────────────

  if (phase === 'loading') return <div className="std"><div className="std-wrap">Loading…</div></div>

  if (phase === 'login') {
    return (
      <div className="std"><div className="std-wrap"><div className="std-card">
        <h2>Please sign in</h2>
        <p>You need to be signed in to take the Software Testing diagnostic.</p>
        <Link className="std-btn" to="/login">Sign in</Link>
      </div></div></div>
    )
  }

  if (phase === 'error') {
    return <div className="std"><div className="std-wrap"><div className="std-alert std-alert-bad">{error}</div></div></div>
  }

  if (phase === 'result') return <ResultView result={result} />

  if (phase === 'submitting') {
    return (
      <div className="std"><div className="std-wrap"><div className="std-card">
        <h2>Submitting and grading…</h2>
        <p className="std-muted">Running your code against the hidden tests. This can take up to a minute — please keep this page open.</p>
      </div></div></div>
    )
  }

  if (phase === 'intro') {
    return (
      <div className="std"><div className="std-wrap">
        <h1>Software Testing — Layer 1 diagnostic</h1>
        <p className="std-muted">This is not a pass/fail test. It shows where you should start on programming fundamentals and craft.</p>

        <div className="std-card">
          <h2>What it covers ({me.durationMinutes / 60} hours)</h2>
          <table className="std-table">
            <tbody>
              {me.sections.map(s => (
                <tr key={s.id}><td style={{ width: 40 }}><strong>{s.id}</strong></td><td><strong>{s.title}</strong><div className="std-muted std-small">{s.skill}</div></td><td className="std-muted std-small" style={{ width: 90 }}>~{s.suggestedMinutes} min</td></tr>
              ))}
            </tbody>
          </table>
          <p className="std-small std-muted" style={{ marginTop: 10 }}>You can move between sections freely. Answers save automatically. The timer keeps running if you close the page, and the diagnostic submits itself when time is up.</p>
        </div>

        <div className="std-card">
          <h2>Language</h2>
          <p className="std-muted std-small">Code questions will be in this language. You cannot change it after starting.</p>
          <div style={{ display: 'flex', gap: 10 }}>
            {me.languages.map(l => (
              <label key={l} className={`std-option ${language === l ? 'std-option-selected' : ''}`} style={{ flex: 1 }}>
                <input type="radio" name="language" checked={language === l} onChange={() => setLanguage(l)} />
                <span>{LANGUAGE_LABELS[l]}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="std-card">
          <h2>Proctoring</h2>
          <ul style={{ paddingLeft: 20 }}>
            <li>Your <strong>camera, microphone and entire screen</strong> are streamed live to the proctors while you take the diagnostic. Nothing is recorded.</li>
            <li>The exam runs in fullscreen. If you leave fullscreen or switch to another window, the exam pauses until you come back.</li>
            <li>If your camera, microphone or screen share stops, the exam is blocked until you turn it back on. The timer keeps running.</li>
            <li>Use desktop <strong>Chrome or Edge</strong>, in a quiet place, with one display connected.</li>
          </ul>
          {!browserSupported && <div className="std-alert std-alert-bad">This browser cannot be proctored. Please open this page in desktop Chrome or Edge.</div>}
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 10 }}>
            <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} style={{ marginTop: 4 }} />
            <span>I agree to my camera, microphone and screen being streamed live to the proctors for the duration of this diagnostic.</span>
          </label>
          <button className="std-btn" style={{ marginTop: 14 }} disabled={!consent || !browserSupported} onClick={() => setPhase('setup')}>Continue to setup</button>
        </div>
      </div></div>
    )
  }

  if (phase === 'setup') {
    const resuming = !!me?.attempt
    return (
      <div className="std"><div className="std-wrap">
        <h1>{resuming ? 'Resume your diagnostic' : 'Set up proctoring'}</h1>
        {resuming && <div className="std-alert std-alert-warn">Your diagnostic is in progress and the timer is still running. Turn proctoring back on to continue.</div>}
        {!browserSupported && <div className="std-alert std-alert-bad">This browser cannot be proctored. Please open this page in desktop Chrome or Edge.</div>}
        {!status.singleDisplay && <div className="std-alert std-alert-bad">More than one display is connected. Disconnect the extra monitor (or set Windows to show only one display), then continue.</div>}
        <div className="std-card std-steps">
          <div className={`std-step ${status.camera && status.mic ? 'std-step-done' : ''}`}>
            <div className="std-step-num">{status.camera && status.mic ? '✓' : 1}</div>
            <div style={{ flex: 1 }}>
              <h3>Camera and microphone</h3>
              {status.camera && status.mic
                ? <VideoPreview stream={session.camStream} className="std-preview" />
                : <button className="std-btn" onClick={() => enable(() => session.enableCamera())}>Enable camera and microphone</button>}
            </div>
          </div>
          <div className={`std-step ${status.screen ? 'std-step-done' : ''}`}>
            <div className="std-step-num">{status.screen ? '✓' : 2}</div>
            <div style={{ flex: 1 }}>
              <h3>Share your entire screen</h3>
              {status.screen
                ? <p className="std-muted" style={{ margin: 0 }}>Entire screen is being shared.</p>
                : <>
                    <p className="std-muted std-small" style={{ margin: '0 0 8px' }}>In the dialog, choose the <strong>Entire screen</strong> tab. Windows and tabs are not accepted.</p>
                    <button className="std-btn" onClick={() => enable(() => session.enableScreen())}>Share entire screen</button>
                  </>}
            </div>
          </div>
          <div className="std-step">
            <div className="std-step-num">3</div>
            <div style={{ flex: 1 }}>
              <h3>{resuming ? 'Resume' : 'Start'} in fullscreen</h3>
              {!resuming && <p className="std-muted std-small" style={{ margin: '0 0 8px' }}>Language: <strong>{LANGUAGE_LABELS[language]}</strong>. The 2-hour timer starts when you click.</p>}
              <button className="std-btn" disabled={!session.ready()} onClick={startExam}>{resuming ? 'Resume diagnostic' : 'Start diagnostic'}</button>
            </div>
          </div>
        </div>
        {error && <div className="std-alert std-alert-bad">{error}</div>}
      </div></div>
    )
  }

  // phase === 'exam'
  const section = sections.find(s => s.id === activeSection) || sections[0]
  const blocker = !status.camera || !status.mic ? 'camera'
    : !status.screen ? 'screen'
    : !status.singleDisplay ? 'displays'
    : !status.fullscreen ? 'fullscreen'
    : status.leftWindow ? 'away'
    : null
  const lowTime = remaining < 10 * 60 * 1000

  return (
    <div className="std std-exam">
      <div className="std-topbar">
        <div className="std-wrap std-topbar-row">
          <div>
            <strong>Software Testing diagnostic</strong>
            <span className="std-muted std-small"> · {LANGUAGE_LABELS[attempt.language]} · {progress.answered}/{progress.total} answered · {saveState === 'saved' ? 'All changes saved' : saveState === 'saving' ? 'Saving…' : saveState === 'error' ? 'Not saved — retrying' : 'Unsaved changes'}</span>
          </div>
          <div className="std-status std-small">
            <span><span className={`std-dot ${status.camera ? 'std-dot-ok' : 'std-dot-bad'}`} />Camera</span>
            <span><span className={`std-dot ${status.mic ? 'std-dot-ok' : 'std-dot-bad'}`} />Mic</span>
            <span><span className={`std-dot ${status.screen ? 'std-dot-ok' : 'std-dot-bad'}`} />Screen</span>
            <span title={status.connected ? `${status.proctors} proctor(s) watching` : 'Not connected to the proctoring server'}>
              <span className={`std-dot ${status.connected ? 'std-dot-ok' : 'std-dot-bad'}`} />Proctor
            </span>
            <VideoPreview stream={session.camStream} className="std-selfview" />
            <span className={`std-timer ${lowTime ? 'std-timer-low' : ''}`}>{formatClock(remaining)}</span>
            <button className="std-btn std-btn-sm" onClick={() => setConfirmSubmit(true)}>Submit</button>
          </div>
        </div>
        <div className="std-wrap std-tabs">
          {sections.map(s => (
            <button key={s.id} className={`std-tab ${s.id === section.id ? 'std-tab-active' : ''}`} onClick={() => { setActiveSection(s.id); window.scrollTo(0, 0) }}>
              {s.id}. {s.title}<small>{progress.bySection[s.id]?.done}/{progress.bySection[s.id]?.total}</small>
            </button>
          ))}
        </div>
      </div>

      <div className="std-wrap">
        {error && <div className="std-alert std-alert-bad">{error}</div>}
        <div className="std-card" style={{ background: 'var(--std-accent-soft)', borderColor: 'transparent' }}>
          <h2 style={{ marginBottom: 4 }}>{section.id}. {section.title} <span className="std-muted std-small" style={{ fontWeight: 400 }}>· suggested {section.suggestedMinutes} min</span></h2>
          <p style={{ margin: 0 }}>{section.intro}</p>
        </div>

        {section.questions.map((q, i) => {
          const common = { key: q.id, q, value: answers[q.id], onChange: v => setAnswer(q.id, v) }
          if (q.type === 'mcq') return <McqQuestion {...common} index={i + 1} />
          if (q.type === 'tests') return <TestsQuestion {...common} />
          if (q.type === 'review') return <ReviewQuestion {...common} />
          return (
            <CodeQuestion {...common} language={attempt.language}
              hypothesis={hypotheses[q.id]} onHypothesis={text => saveHypothesis(q.id, text)}
              onRun={(code, stdin) => runCode(q, code, stdin)} />
          )
        })}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          {(() => {
            const idx = sections.findIndex(s => s.id === section.id)
            return (
              <>
                <button className="std-btn std-btn-ghost" disabled={idx === 0} onClick={() => { setActiveSection(sections[idx - 1].id); window.scrollTo(0, 0) }}>← Previous section</button>
                {idx < sections.length - 1
                  ? <button className="std-btn" onClick={() => { setActiveSection(sections[idx + 1].id); window.scrollTo(0, 0) }}>Next section →</button>
                  : <button className="std-btn" onClick={() => setConfirmSubmit(true)}>Review and submit</button>}
              </>
            )
          })()}
        </div>
      </div>

      {blocker && (
        <div className="std-overlay">
          <div className="std-card">
            <h2>{{
              fullscreen: 'Return to fullscreen',
              screen: 'Screen sharing stopped',
              camera: 'Camera or microphone stopped',
              displays: 'More than one display connected',
              away: 'You left the exam window'
            }[blocker]}</h2>
            {blocker === 'away'
              ? <p>You were away for <strong>{status.leftWindow.seconds} seconds</strong>. Stay in this window until you submit. The timer is still running ({formatClock(remaining)} left).</p>
              : <p>The diagnostic is paused until proctoring is back on. <strong>The timer is still running</strong> ({formatClock(remaining)} left).</p>}
            {error && <div className="std-alert std-alert-bad">{error}</div>}
            {blocker === 'displays' && <p className="std-small">Disconnect the extra monitor, or press <strong>Win + P</strong> and choose “PC screen only”. The exam continues automatically.</p>}
            {blocker === 'away' && <button className="std-btn" onClick={() => session.acknowledgeReturn()}>Return to the diagnostic</button>}
            {blocker === 'camera' && <button className="std-btn" onClick={() => enable(() => session.enableCamera())}>Turn camera and microphone back on</button>}
            {blocker === 'screen' && <button className="std-btn" onClick={() => enable(() => session.enableScreen())}>Share entire screen again</button>}
            {blocker === 'fullscreen' && <button className="std-btn" onClick={() => document.documentElement.requestFullscreen().catch(() => {})}>Go fullscreen</button>}
          </div>
        </div>
      )}

      {confirmSubmit && !blocker && (
        <div className="std-overlay">
          <div className="std-card">
            <h2>Submit the diagnostic?</h2>
            <p>You have answered {progress.answered} of {progress.total} questions.</p>
            <ul className="std-small">
              {sections.map(s => <li key={s.id}>{s.id}. {s.title}: {progress.bySection[s.id].done}/{progress.bySection[s.id].total}</li>)}
            </ul>
            <p className="std-muted std-small">You cannot change your answers after submitting.</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="std-btn" onClick={submit}>Submit now</button>
              <button className="std-btn std-btn-ghost" onClick={() => setConfirmSubmit(false)}>Keep working</button>
            </div>
          </div>
        </div>
      )}

      {confirmExit && (
        <div className="std-overlay">
          <div className="std-card">
            <h2>Do you really want to exit the diagnostic?</h2>
            <p>Your camera and screen sharing will stop. Your answers are saved, but <strong>the timer keeps running</strong> ({formatClock(remaining)} left) and you will have to set up proctoring again to continue.</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="std-btn" onClick={() => setConfirmExit(false)}>Stay in the exam</button>
              <button className="std-btn std-btn-danger" onClick={leaveExam}>Exit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ResultView({ result }) {
  if (!result || result.pending) {
    return (
      <div className="std"><div className="std-wrap"><div className="std-card">
        <h2>Diagnostic submitted</h2>
        <p>Your answers are in. Grading is still being finished — check back later for your results.</p>
      </div></div></div>
    )
  }
  const { sections, startHere, items, pendingReview } = result
  return (
    <div className="std"><div className="std-wrap">
      <h1>Your diagnostic results</h1>
      <p className="std-muted">This shows where to start, not a pass or fail.{pendingReview && ' Some answers are still waiting for a reviewer, so these results may change.'}</p>

      {startHere.length > 0 && (
        <div className="std-card" style={{ background: 'var(--std-accent-soft)', borderColor: 'transparent' }}>
          <h2>Where to start</h2>
          {startHere.map(s => (
            <div key={s.section} style={{ margin: '10px 0' }}>
              <strong>{s.section}. {s.title}</strong>
              <p style={{ margin: '4px 0 0' }}>{s.advice}</p>
            </div>
          ))}
        </div>
      )}

      <div className="std-card">
        <h2>By skill</h2>
        <table className="std-table">
          <tbody>
            {Object.entries(sections).map(([id, s]) => (
              <tr key={id}>
                <td style={{ width: '45%' }}><strong>{id}. {s.title}</strong><div className="std-muted std-small">{s.skill}</div></td>
                <td style={{ width: '35%' }}><div className="std-bar"><div style={{ width: `${s.percent}%` }} /></div><span className="std-small std-muted">{s.percent}%</span></td>
                <td><span className={`std-badge std-level-${s.level}`}>{s.level}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="std-card">
        <h2>Feedback</h2>
        {['B1', 'B2', 'C1', 'C2'].filter(id => items[id]).map(id => (
          <div key={id} style={{ margin: '10px 0' }}>
            <strong>{id}</strong>: {items[id].pending
              ? <span className="std-muted">hidden tests could not be run yet — your answer is saved and will be graded later</span>
              : `${items[id].passed}/${items[id].total} hidden tests passed`}
            {items[id].hypothesisFeedback && <div className="std-muted std-small">Hypothesis: {items[id].hypothesisFeedback}</div>}
          </div>
        ))}
        {items.D1 && (
          <div style={{ margin: '10px 0' }}>
            <strong>D1</strong>: your tests caught {items.D1.killed} of {items.D1.mutantCount} broken implementations; {items.D1.validRows} of {items.D1.totalRows} tests matched the spec.
          </div>
        )}
        {items.F1?.feedback && <div style={{ margin: '10px 0' }}><strong>F1</strong>: {items.F1.feedback}</div>}
      </div>
    </div></div>
  )
}
